import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatNumber, formatPercentage } from "../utils/numbers";

const AnimatedDigit: React.FC<{ digit: string; isAnimating: boolean }> = ({
  digit,
  isAnimating,
}) => {
  return <span className={`billboard-number ${isAnimating ? "animating" : ""}`}>{digit}</span>;
};

export const AnimatedNumber: React.FC<{ value: number; type?: "pct" }> = ({ value, type }) => {
  const [animatingDigits, setAnimatingDigits] = useState<Set<number>>(new Set());
  const prevValue = useRef(value);

  const formatFn = useMemo(() => {
    if (type === "pct") {
      return formatPercentage;
    }

    return formatNumber;
  }, [type]);

  const formattedValue = formatFn(value);
  const prevFormattedValue = useRef(formattedValue);

  useEffect(() => {
    if (prevValue.current !== value) {
      const newFormatted = formatFn(value);
      const oldFormatted = prevFormattedValue.current;

      const newAnimatingDigits = new Set<number>();

      // Compare each character position to see which digits changed
      const maxLength = Math.max(newFormatted.length, oldFormatted.length);
      for (let i = 0; i < maxLength; i++) {
        const newChar = newFormatted[i] || "";
        const oldChar = oldFormatted[i] || "";
        if (newChar !== oldChar && /\d/.test(newChar)) {
          newAnimatingDigits.add(i);
        }
      }

      setAnimatingDigits(newAnimatingDigits);

      const timer = setTimeout(() => setAnimatingDigits(new Set()), 600);

      prevValue.current = value;
      prevFormattedValue.current = newFormatted;

      return () => clearTimeout(timer);
    }
  }, [value, formatFn]);

  return (
    <span>
      {formattedValue.split("").map((char, index) => (
        <AnimatedDigit key={index} digit={char} isAnimating={animatingDigits.has(index)} />
      ))}
    </span>
  );
};
